import { useEffect, useRef, useState } from "react";
import { type Control, useController } from "react-hook-form";
import { tw } from "@careinfo/tailwind";
import _File_ from "../../../utils/_File_";
import CameraIcon from "../../../Icons/CameraIcon";
import PlusIcon from "../../../Icons/PlusIcon";
import "./style.css";

interface Props {
  name: string;
  control: Control;
  label?: string;
  placeholder?: string;
}

export default function ImageField({
  name,
  control,
  label = "",
  placeholder = "",
}: Props) {
  const {
    field: { value = null, onChange },
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: "",
  });

  const [imagePreview, setImagePreview] = useState(value);

  const renderPreviewImage = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const file = files[0] as File;
    if (files.length > 0 && _File_.isImage(file)) {
      onChange(file);
      renderPreviewImage(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const file = files[0] as File;
    if (files.length > 0 && _File_.isImage(file)) {
      onChange(file);
      renderPreviewImage(file);
    }
  };

  const removeImage = () => {
    onChange(null);
    setImagePreview(null);
  };

  useEffect(() => {
    setImagePreview(value);
  }, [value]);

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      className={tw` mt-2 relative`}
    >
      <label
        htmlFor={name}
        className={tw`text-[16px] font-[500] leading-[22px] text-left text-[#030303] pb-2`}
      >
        {label}
      </label>
      <label
        htmlFor={name}
        className={tw`input_field w-full h-[144px]  relative p-[20px] gap-[10px] border-2 border-[#00000033] rounded-[4px] flex flex-col justify-center items-center cursor-pointer`}
      >
        <input
          type="file"
          className={tw`opacity-0 cursor-pointer hidden `}
          accept="image/*"
          onChange={handleImageChange}
          id={name}
        />
        {imagePreview ? (
          <>
            <button
              onClick={() => removeImage()}
              type="button"
              className={tw`absolute top-4 right-4 block h-6 w-6 rotate-[45deg] rounded-full text-primary focus:outline-none focus-visible:ring focus-visible:ring-primary-light`}
            >
              <PlusIcon />
            </button>
            <img
              src={imagePreview}
              alt="Product"
              className={tw`h-full w-full object-contain`}
            />
          </>
        ) : (
          <>
            <div
              className={tw`w-12 h-12 text-[#CCCCCC] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2`}
            >
              <CameraIcon />
              <p>{placeholder}</p>
            </div>
          </>
        )}
      </label>
      {error && (
        <div className={tw`text-red-600 text-[12px]`}>{error.message}</div>
      )}
    </div>
  );
}
